import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Container,
  Paper,
  Divider,
  Alert,
} from '@mui/material';
import {
  CheckCircle,
  ErrorOutline,
  Download,
  School,
  CalendarToday,
} from '@mui/icons-material';
import { validateCertificate } from '../services/api.js';

const CertificateValidationPage = () => {
  const { validationCode } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [containerWidth, setContainerWidth] = useState(null);
  const pdfContainerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (pdfContainerRef.current) {
        setContainerWidth(pdfContainerRef.current.clientWidth);
      }
    };
    
    // Initial check
    setTimeout(handleResize, 100);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [loading, error]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await validateCertificate(validationCode);
        setData(result);
      } catch (err) {
        setError(err.message || 'Código de validação inválido ou não encontrado.');
      } finally {
        setLoading(false);
      }
    };
    if (validationCode) fetchData();
  }, [validationCode]);

  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const cleanBaseUrl = apiBaseUrl.replace(/\/+$/, '');
  // Removed #toolbar=0 etc as they only apply to iframes
  const pdfUrl = `${cleanBaseUrl}/api/certificados/validar/${validationCode}/pdf`;

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.target = '_blank';
    link.download = `certificado_${validationCode}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    } catch {
      return dateString;
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      
      <Box sx={{ flexGrow: 1, py: { xs: 4, md: 8 }, px: { xs: 2, md: 4 } }}>
        <Container maxWidth="xl">
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: 'center', mt: 8 }}>
              <ErrorOutline sx={{ fontSize: 80, color: 'error.main', mb: 2 }} />
              <Typography variant="h4" fontWeight="bold" gutterBottom>Certificado não encontrado</Typography>
              <Typography color="text.secondary" sx={{ mb: 4 }}>O código fornecido é inválido ou o certificado não existe mais.</Typography>
              <Button variant="contained" onClick={() => navigate('/')}>Ir para Início</Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {/* Header Status */}
              <Box sx={{ textAlign: 'center' }}>
                {data.valid ? (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: '#dcfce7', color: '#166534', px: 3, py: 1, borderRadius: '24px', mb: 3 }}>
                    <CheckCircle />
                    <Typography fontWeight="bold">Certificado Válido</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: '#fee2e2', color: '#991b1b', px: 3, py: 1, borderRadius: '24px', mb: 3 }}>
                    <ErrorOutline />
                    <Typography fontWeight="bold">Certificado Inválido</Typography>
                  </Box>
                )}
                
                <Typography variant="h3" fontWeight="900" sx={{ color: '#0f172a', mb: 1 }}>
                  Certificado de Conclusão
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
                  Autenticidade garantida pela plataforma
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', lg: 'row' }, gap: 4, alignItems: 'flex-start' }}>
                {/* PDF Preview Frame */}
                <Paper 
                  elevation={0}
                  sx={{ 
                    flex: { xs: '1', lg: '2.5' }, 
                    width: '100%',
                    maxHeight: { xs: 'auto', lg: '85vh' },
                    borderRadius: '24px', 
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
                    bgcolor: '#F0F4F8',
                    p: { xs: 2, md: 3 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Box ref={pdfContainerRef} sx={{ 
                    width: '100%', 
                    border: '1px solid',
                    borderColor: 'divider',
                    overflow: 'hidden',
                    display: 'flex',
                    justifyContent: 'center',
                    bgcolor: 'white'
                  }}>
                    <Document
                      file={pdfUrl}
                      loading={
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                          <CircularProgress />
                        </Box>
                      }
                      error={
                        <Typography color="error" sx={{ p: 4, textAlign: 'center' }}>
                          Falha ao carregar o visualizador do certificado.
                        </Typography>
                      }
                    >
                      <Page 
                        pageNumber={1} 
                        width={containerWidth ? containerWidth : undefined}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                      />
                    </Document>
                  </Box>
                </Paper>

                {/* Info Sidebar */}
                <Paper 
                  elevation={0}
                  sx={{ 
                    flex: '1', 
                    p: 4, 
                    borderRadius: '24px', 
                    border: '1px solid',
                    borderColor: 'divider',
                    height: 'fit-content'
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    Detalhes do Certificado
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold" textTransform="uppercase">
                        Aluno
                      </Typography>
                      <Typography variant="body1" fontWeight="bold" sx={{ color: '#0f172a' }}>
                        {data.student_name}
                      </Typography>
                    </Box>
                    
                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold" textTransform="uppercase">
                        Curso
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.5 }}>
                        <School sx={{ color: 'primary.main', fontSize: 20 }} />
                        <Typography variant="body1" fontWeight="bold" sx={{ color: '#0f172a' }}>
                          {data.course_name}
                        </Typography>
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold" textTransform="uppercase">
                        Data de Conclusão
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 0.5 }}>
                        <CalendarToday sx={{ color: 'primary.main', fontSize: 20 }} />
                        <Typography variant="body1" sx={{ color: '#0f172a' }}>
                          {formatDate(data.conclusion_date)}
                        </Typography>
                      </Box>
                    </Box>

                    <Box>
                      <Typography variant="caption" color="text.secondary" fontWeight="bold" textTransform="uppercase">
                        Código de Autenticação
                      </Typography>
                      <Box sx={{ bgcolor: '#f1f5f9', p: 1.5, borderRadius: 2, mt: 0.5 }}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#334155', wordBreak: 'break-all' }}>
                          {validationCode}
                        </Typography>
                      </Box>
                    </Box>

                    {data.invalid_reason && (
                      <Alert severity="error" sx={{ borderRadius: 2 }}>
                        {data.invalid_reason}
                      </Alert>
                    )}

                    <Button 
                      variant="contained" 
                      color="primary"
                      size="large"
                      startIcon={<Download />}
                      onClick={handleDownload}
                      disabled={!data.pdf_available}
                      sx={{ 
                        mt: 2, 
                        py: 1.5, 
                        borderRadius: '12px',
                        fontWeight: 'bold'
                      }}
                    >
                      Fazer Download (PDF)
                    </Button>
                  </Box>
                </Paper>
              </Box>
            </Box>
          )}
        </Container>
      </Box>

    </Box>
  );
};

export default CertificateValidationPage;
